import type { InputType } from './stage';
import { dueKey } from '../time/dueDate';

/**
 * Finding a case in a busy queue. Section 3 of the request of 5 October 2026:
 * a technician searches their queue, sorts it and narrows it, and a count says
 * how many cases are left.
 *
 * The filters here; the sorts are the case sorts every other list uses
 * (`caseSort.ts`), so Urgent first means the same thing in a queue as on Orders.
 * Pure functions over the cases the screen already holds: a queue is one
 * person's cases and one department's, not a page of the lab.
 */

/** What a case must carry to be searched and narrowed. */
export interface QueueCase {
  id: string;
  jobOrderNo: string;
  product: string;
  input: InputType;
  urgent: boolean;
  rework: boolean;
  held: boolean;
  /** The lab day the case is due, YYYY-MM-DD. */
  deliveryDate: string;
  /**
   * Absent for a caller without seeIdentity: the server leaves both names out,
   * so a technician's search can never confirm a name they may not read.
   */
  dentistName?: string | null;
  patientName?: string | null;
}

export const QUEUE_STATUSES = ['any', 'rework', 'held'] as const;
export type QueueStatusFilter = (typeof QUEUE_STATUSES)[number];

export const QUEUE_DUE = ['any', 'today', 'overdue'] as const;
export type QueueDueFilter = (typeof QUEUE_DUE)[number];

export type QueueTypeFilter = 'any' | InputType;

export interface QueueFilters {
  search: string;
  urgentOnly: boolean;
  status: QueueStatusFilter;
  caseType: QueueTypeFilter;
  due: QueueDueFilter;
}

export const NO_QUEUE_FILTERS: QueueFilters = {
  search: '',
  urgentOnly: false,
  status: 'any',
  caseType: 'any',
  due: 'any',
};

/**
 * Whether a case matches what was typed: the job order number, the tray's own
 * number, the item, and the patient's and the dentist's names for somebody who
 * is sent them. Any part of any of them, without regard to case.
 */
export function matchesQueueSearch(query: string, kit: QueueCase): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return [kit.jobOrderNo, kit.id, kit.product, kit.patientName, kit.dentistName].some(
    (value) => typeof value === 'string' && value.toLowerCase().includes(needle),
  );
}

function matchesStatus(status: QueueStatusFilter, kit: QueueCase): boolean {
  if (status === 'rework') return kit.rework;
  if (status === 'held') return kit.held;
  return true;
}

/** Due today is the lab's day itself; overdue is any day before it. The one rule of dueDate.ts. */
function matchesDue(due: QueueDueFilter, kit: QueueCase, today: string): boolean {
  if (due === 'any') return true;
  return dueKey(kit.deliveryDate, today) === due;
}

/** The cases that pass every filter that is set, in the order they came. */
export function filterQueue<Case extends QueueCase>(
  kits: readonly Case[],
  filters: QueueFilters,
  /** The lab's day, YYYY-MM-DD: what the Due filter measures against. */
  today: string,
): Case[] {
  return kits.filter(
    (kit) =>
      matchesQueueSearch(filters.search, kit) &&
      (!filters.urgentOnly || kit.urgent) &&
      matchesStatus(filters.status, kit) &&
      (filters.caseType === 'any' || kit.input === filters.caseType) &&
      matchesDue(filters.due, kit, today),
  );
}

/** How many of the four filters are set, for the badge on the Filters button. Search is not one. */
export function activeQueueFilters(filters: QueueFilters): number {
  return (
    Number(filters.urgentOnly) +
    Number(filters.status !== 'any') +
    Number(filters.caseType !== 'any') +
    Number(filters.due !== 'any')
  );
}

/** Whether anything is narrowing the queue, a search included: whether Reset has anything to do. */
export function narrowsQueue(filters: QueueFilters): boolean {
  return filters.search.trim() !== '' || activeQueueFilters(filters) > 0;
}
